import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet";
import { Bell, Calendar, Globe, Info, Mail, PlusIcon, User } from "lucide-react";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { getRouteApi } from "@tanstack/react-router";
// import { Skeleton } from "@workspace/web-ui/components/skeleton";
import { Image } from "@workspace/web-ui/components/image";
import { AppointmentStatusCard } from "../cards/appointment-status";
import { Tasks } from "~/features/dashboard/components/tasks";
import { toast } from "sonner";
import { useUpdateAppointmentMutation } from "../../api/mutations/appointment";
// import { useQuery } from "@tanstack/react-query";
// import { getQuickActionsAppointmentByIdQueryOptions } from "../../api/queries/quick-actions";
import { FormattedEncounter } from "../../../encounters/types";
import { EncounterStatus } from "../../../encounters/components/encounter-status";
import { cn } from "@workspace/web-ui/lib/utils";
import { useState, useEffect } from "react";
import { getQueryClient } from "~/providers/query.provider";
import { quickActionsKeys } from "../../api/queries";
import { encountersKeys } from "../../../encounters/api/queries";
import TaskContainer from "~/features/dashboard/components/tasks/task-container";
import { NotesContainer } from "~/features/dashboard/components/notes";
import { PatientDetailsConditional } from "~/features/dashboard/components/patient/patient-details-conditional";
import { useSendActionNeededMutation } from "../../../patient-logs/api/mutations";
import { ResolveToggle } from "~/features/dashboard/components/resolve-toggle";

interface InfoRowProps {
    icon: React.ElementType;
    value: string;
    testId: string;
}

const InfoRow = ({ icon: Icon, value, testId }: InfoRowProps) => {
    if (!value) return null;

    return (
        <div className="flex gap-2 items-center" data-testid={testId}>
            <Icon className="w-4 h-4 text-[#7F8493]" />
            <h2 className="text-sm font-normal text-[#2D2F35]">{value}</h2>
        </div>
    );
};

interface AppointmentModalProps {
    open: boolean;
    onOpenChange: (value: boolean) => void;
    encounter: FormattedEncounter;
    type: string
}

export const AppointmentModal = ({ open, onOpenChange, encounter, type }: AppointmentModalProps) => {
    const queryClient = getQueryClient()
    const [isOpen, setIsOpen] = useState(false);
    const [localStatus, setLocalStatus] = useState(encounter.status);
    const [localTag, setLocalTag] = useState(encounter.tag);
    const [statusLoading, setStatusLoading] = useState(false);
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();

    useEffect(() => {
        setLocalStatus(encounter.status);
        setLocalTag(encounter.tag);
    }, [encounter.status, encounter.tag]);

    // Check if meeting has passed by combining date and time
    const hasMeetingPassed = (() => {
        try {
            // Assuming date format is like "Dec 24, 2024" and time is like "2:30 PM"
            const meetingDateTime = new Date(`${encounter.date} ${encounter.time}`);
            const now = new Date();
            return meetingDateTime < now;
        } catch (error) {
            // If date parsing fails, don't hide the section
            console.warn("Failed to parse meeting date/time:", { date: encounter.date, time: encounter.time }, error);
            return false;
        }
    })();

    // const { data, isLoading } = useQuery(getQuickActionsAppointmentByIdQueryOptions(encounter.id ?? ""));

    const UpdateAppointmentMutation = useUpdateAppointmentMutation({
        id: encounter?.id ?? "",
    });

    const { mutate: sendActionNeeded, isPending: sendActionpending } = useSendActionNeededMutation();

    const PATIENT_CONFIG = [
        {
            icon: User,
            getValue: () => encounter?.patient.name,
            testId: "patient-name",
        },
        {
            icon: Mail,
            getValue: () => encounter?.patient.email,
            testId: "patient-email",
        },
        {
            icon: Calendar,
            getValue: () => encounter?.meetingStartingTime,
            testId: "patient-meeting",
        },
        {
            icon: Globe,
            getValue: () => encounter?.timezone,
            testId: "patient-timezone",
        },
        {
            icon: Bell,
            getValue: () => `${encounter?.date}`,
            testId: "patient-notification",
        },
    ];

    const handleMarkAppointmentStatus = async (status: 'attended' | 'cancelled') => {
        const meetingStatus = status === "cancelled" ? "not-held" : "held";

        // Optimistic update - update UI immediately
        const previousStatus = localStatus;
        const previousTag = localTag;
        setLocalStatus(status);
        setLocalTag(status === "attended" ? "Held" : "Not Held");
        setStatusLoading(true);

        try {
            await UpdateAppointmentMutation.mutateAsync({
                status,
            }, {
                onSuccess(data) {
                    if (data.status === "success") {
                        toast.success(`Appointment marked as ${meetingStatus}`, {
                            style: {
                                background: "#6EE7B7",
                                color: "#FFFFFF",
                                borderColor: "#6EE7B7",
                                "& svg": {
                                    color: "#FFFFFF",
                                },
                            } as React.CSSProperties,
                        });
                    } else {
                        // Rollback if API response indicates failure
                        setLocalStatus(previousStatus);
                        setLocalTag(previousTag);
                        toast.error(`Failed to update appointment status`);
                    }
                    setStatusLoading(false);
                    queryClient.invalidateQueries({ queryKey: [quickActionsKeys.appointmentById(encounter.id ?? ""), encountersKeys.all] })
                },
                onError() {
                    // Rollback on error
                    setLocalStatus(previousStatus);
                    setLocalTag(previousTag);
                    setStatusLoading(false);
                    toast.error(`An error occurred while updating appointment status as ${meetingStatus}`);
                },
            });
        } catch (error) {
            // Rollback on exception
            setLocalStatus(previousStatus);
            setLocalTag(previousTag);
            setStatusLoading(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                title="upcoming-meeting"
                side="right"
                className="flex flex-col gap-6 overflow-y-auto p-4 sm:p-6 sm:pb-40 sm:max-w-[600px] w-full focus:outline-none"
                aria-describedby="upcoming-details-title"
            >
                {
                    encounter && (
                        <div className="flex flex-col h-full gap-4 mt-6">
                            <div className="flex flex-col gap-2 w-full flex-wrap justify-between">
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <EncounterStatus status={localStatus} tag={localTag} loading={statusLoading} />
                                        {hasMeetingPassed && (
                                            <div
                                                className={cn(
                                                    "flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit",
                                                    "text-[#FFA726] bg-[#FFF5E6]"
                                                )}
                                                data-testid="action-needed-tag"
                                            >
                                                <Info className="size-4 text-[#FFA726]" />
                                                <span className="text-xs font-medium">Action Needed</span>
                                            </div>
                                        )}
                                    </div>
                                    <ResolveToggle encounterId={encounter.encounterId} recommendedTasks={encounter.recommendedTasks} />
                                </div>
                                <SheetHeader>
                                    <SheetTitle
                                        id="appointment-title"
                                        className="text-lg sm:text-xl font-medium"
                                    >
                                        <div className="flex flex-row gap-2 sm:gap-3 sm:items-center items-start">
                                            {encounter.patient.name} {encounter.subtitle ? encounter.subtitle : "Appointment"}
                                            {program === "urgent-care" ? (
                                                encounter.patient.urgent_care_onboarded && encounter.patient.identity_verified && (
                                                    <div className="flex-shrink-0 mt-1 sm:mt-0">
                                                        <Image src="/icons/check.svg" alt="timeline" className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                                                    </div>
                                                )
                                            ) : (
                                                encounter.patient.medical_plan?.payment_status === "paid" &&
                                                encounter.patient.glp1_guide_accepted &&
                                                encounter.patient.identity_verified &&
                                                encounter.patient.supplementary_health_assessment &&
                                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                                    <div className="flex-shrink-0 mt-1 sm:mt-0">
                                                        <Image src="/icons/check.svg" alt="timeline" className="w-4 h-4 sm:w-5 sm:h-5" layout="constrained" width={96} height={96} />
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </SheetTitle>
                                </SheetHeader>
                            </div>

                            <div className="flex flex-col md:flex-row gap-2 items-end justify-between">
                                <div className="flex flex-col gap-3 w-full">

                                    {PATIENT_CONFIG.map(({ icon, getValue, testId }) => {
                                        const value = getValue();
                                        return value ? (
                                            <InfoRow
                                                key={testId}
                                                icon={icon}
                                                value={value}
                                                testId={testId}
                                            />
                                        ) : null;
                                    })}
                                </div>
                                <a
                                    href={encounter?.meetingLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={cn(
                                        buttonVariants({ variant: "default" }),
                                        "bg-[#4272DD] rounded-lg w-full sm:w-auto",
                                        localStatus !== "happening"
                                            ? "pointer-events-none cursor-not-allowed opacity-60 no-underline"
                                            : ""
                                    )}
                                >
                                    Join meeting
                                </a>
                            </div>

                            {encounter.description && (
                                <div
                                    className="flex flex-col gap-2 p-4 bg-[#F8F9FA] border border-[#E9ECEF] rounded-lg"
                                    data-testid="appointment-description"
                                >
                                    <h3 className="text-xs font-medium uppercase text-[#6C757D] tracking-wide flex items-center gap-2">
                                        <Info className="w-4 h-4 text-[#6C757D]" aria-label="Info" />
                                        Patient's Request
                                    </h3>
                                    <p className="text-sm text-[#2D2F35] leading-relaxed whitespace-pre-wrap">
                                        {encounter.description}
                                    </p>
                                </div>
                            )}

                            <PatientDetailsConditional
                                encounter={encounter}
                                onOpenChange={onOpenChange}
                                program={program}
                                type={type}
                                onStatusChange={(status, tag) => {
                                    setLocalStatus(status);
                                    setLocalTag(tag);
                                }}
                            />
                            {/* {program !== "urgent-care" && !hasMeetingPassed && (
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 w-full bg-[#EBF0FB] rounded-lg">
                                    <div className="flex items-end w-full sm:w-fit">
                                        <Info className="size-6 text-[#4272DD]" />
                                    </div>
                                    {localStatus === "happening" ? (
                                        <p className="text-sm font-normal text-[#202020CC]">This Appointment is live.</p>
                                    ) : (
                                        <p className="text-sm font-normal text-[#202020CC] whitespace-normal">This appointment is scheduled for {`${encounter.date}`}. Please ensure you're available 5 minutes before the start time. </p>
                                    )}

                                    {localStatus === "happening" && (
                                        <Link
                                            className={cn(buttonVariants({ variant: "outline" }), "bg-transparent w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border-none py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                            to="/dashboard/$program/patients/patient/$id"
                                            params={{
                                                id: encounter?.patient.id || "",
                                                program
                                            }}
                                            search={{
                                                name: encounter?.patient.name.replace(/\s+/g, "") || "",
                                                sessionId: encounter?.session?.session_id || ""
                                            }}
                                        >
                                            View Patient Log
                                        </Link>
                                    )}

                                    <a
                                        href={encounter?.meetingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={cn(
                                            buttonVariants({ variant: "default" }),
                                            "bg-[#4272DD] rounded-lg sw-full sm:w-auto",
                                            localStatus !== "happening"
                                                ? "pointer-events-none cursor-not-allowed opacity-60 no-underline"
                                                : ""
                                        )}
                                    >
                                        Join meeting
                                    </a>
                                </div>
                            )} */}

                            {/* {localStatus !== "happening" && localStatus !== "past" && (<div className="flex justify-self-end justify-end">
                                <Link
                                    className={cn(buttonVariants({ variant: "outline" }), "bg-white w-[135px] text-[#4272DD] justify-self-end rounded-[8px] border border-[#DCDCDE] py-3 px-4 hover:bg-[#4272DD] hover:text-white")}
                                    to="/dashboard/patients/patient/$id"
                                    params={{
                                        id: encounter?.patient.id || ""
                                    }}
                                    search={{
                                        name: encounter?.patient.name.replace(/\s+/g, "") || ""
                                    }}
                                >
                                    View Patient Log
                                </Link>
                            </div>)} */}
                            {program !== "urgent-care" && (
                                <div className="flex gap-4 w-full flex-wrap">
                                    {(localStatus === "past" || localStatus === "cancelled") && (
                                        <div className="flex flex-col w-full">
                                            <AppointmentStatusCard
                                                status={localStatus}
                                                encounter={encounter}
                                                onStatusChange={handleMarkAppointmentStatus}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* {["happening", "past", "completed", "attended"].includes(encounter.status) && encounter.patient.urgent_care_onboarded && program === "urgent-care" && (
                                <NotesContainer
                                    encounterId={encounter.encounterId}
                                    patientId={encounter.patient.id}
                                    notes={encounter.notes}
                                    icd={encounter.session?.icd10_code}
                                />
                            )
                            } */}

                            {/* Notes Section */}
                            {(localStatus === "completed" || localStatus === "attended") &&
                                program !== "urgent-care" &&
                                encounter?.patient?.medical_plan?.payment_status === "paid" &&
                                encounter?.patient?.glp1_guide_accepted &&
                                encounter?.patient?.identity_verified &&
                                encounter?.patient?.supplementary_health_assessment &&
                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                    <NotesContainer
                                        encounterId={encounter.encounterId}
                                        patientId={encounter.patient.id}
                                    />
                                )
                            }

                            {(localStatus === "completed" || localStatus === "attended") &&
                                program !== "urgent-care" && (
                                    <>
                                        {!(encounter?.patient?.medical_plan?.payment_status === "paid" &&
                                            encounter?.patient?.glp1_guide_accepted &&
                                            encounter?.patient?.identity_verified &&
                                            encounter?.patient?.supplementary_health_assessment &&
                                            Object.keys(encounter.patient.supplementary_health_assessment).length > 0) && (
                                                <div className="flex items-center bg-[#F0F5FF] rounded-lg px-4 py-2 justify-between w-full mb-8">
                                                    <p className="text-sm text-[#1A202C]">
                                                        Patient must be verified
                                                    </p>
                                                    <Button
                                                        className="button-primary !bg-[#4272DD] h-[44px] !text-white flex gap-2 items-center !shadow-none w-fit !border-none"
                                                        onClick={async () => {
                                                            try {
                                                                await sendActionNeeded({ patient_id: encounter.patient.id });
                                                                toast.success("Reminder sent to patient to complete verification.");
                                                            } catch (error) {
                                                                toast.error("Failed to send reminder.");
                                                            }
                                                        }}
                                                        disabled={sendActionpending}
                                                    >
                                                        {sendActionpending && (
                                                            <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                                            </svg>
                                                        )}
                                                        Send a Reminder
                                                    </Button>
                                                </div>
                                            )}
                                        <div className="pb-20">
                                            {encounter?.patient?.medical_plan?.payment_status === "paid" &&
                                                encounter?.patient?.glp1_guide_accepted &&
                                                encounter?.patient?.identity_verified &&
                                                encounter?.patient?.supplementary_health_assessment &&
                                                Object.keys(encounter.patient.supplementary_health_assessment).length > 0 && (
                                                    <div className="w-full flex justify-between gap-4 items-center">
                                                        <h2 className="text-xl font-medium">Recommended Task</h2>
                                                        <Button className="button-primary !bg-[#4272DD] !text-white !flex !gap-2 !border-none !shadow-none" onClick={() => setIsOpen(true)}>
                                                            <PlusIcon className="w-4 h-4 text-white" />
                                                            Add
                                                        </Button>
                                                    </div>
                                                )}
                                            {isOpen && (
                                                <Tasks open={isOpen} onOpenChange={setIsOpen} id={encounter?.encounterId ?? ""} className="bg-[#EFF0F2]" />
                                            )}
                                            <TaskContainer encounterId={encounter?.encounterId} patientId={encounter?.patient.id} />
                                        </div>
                                    </>
                                )}
                        </div>
                    )
                }
            </SheetContent>
        </Sheet>
    );
};


